<template>
	<span
		class="badge flex--no-shrink"
		:class="`badge--${type || 'default'}`"
	>
		<Inline
			v-if="isLoading || icon"
			:class="{'spin': isLoading}"
			:svg="isLoading ? 'loading' : icon"
		/>
		<slot v-if="$slots.default" />
		<span v-else class="badge__text-container">{{ text }}</span>
	</span>
</template>

<script lang="ts">
	import { defineComponent, type PropType } from 'vue';

	export type BadgeType = 'alt' | 'alert' | 'error' | 'warning' | 'success' | 'concentric' | 'translucent';
	export type BadgeProps = {
		type?: BadgeType;
		icon?: string;
		text: number | string;
		isLoading?: boolean;
	}

	export default defineComponent({
		name: 'CBadge',
		props: {
			type: {
				type: String as PropType<BadgeType>,
				default: null
			},
			icon: {
				type: String,
				default: null
			},
			text: {
				type: [Number, String],
				default: null
			},
			isLoading: {
				type: Boolean,
				default: false
			}
		}
	});
</script>

<style lang="scss" src="@cognitoforms/styles/global/_motion.scss"></style>
<style lang="scss">
.badge {
	display: inline-flex;
	align-items: center;
	flex-shrink: 1;
	justify-content: center;
	border-radius: 3em;
	outline: 1px solid transparent;
	font-weight: normal;
	gap: .2em; // Some sizes/spacing in this component do not use design tokens for a precise badge design
	word-break: keep-all;

	.is-mac &,
	.is-ios & {
		outline: none;
	}

	&__text-container {
		display: inline-block;
		overflow: hidden;
		padding-top: .2em;
		padding-bottom: .2em;
		line-height: 1.4;

		@include nowrap;
	}

	html:not(.website) & {
		min-width: $size__icon--large - 2px;
		height: $size__icon--large - 2px;
		padding: 0 .5em;
		font-size: $size__1;
	}

	// Segoe has extra space which causes text to not look vertically centered
	// https://gitlab.com/gitlab-org/gitlab-foss/-/issues/26059
	html.is-windows:not(.website) & {
		line-height: .9;

		@media (max-resolution: 143dpi) {
			font-size: 12px;
		}
	}

	.website & {
		min-width: 2em;
		min-height: 2em;
		padding: 0 .6em;
		font-size: .7em;
	}

	.icon {
		width: 1em;
		height: auto;

		:root:root & svg {
			color: inherit;
		}
	}

	&--default {
		background-color: var(--shade-2);
		outline-color: var(--shade-6);

		.is-mac &,
		.is-ios & {
			box-shadow: 0 0 0 1px var(--shade-6);
		}

		.icon {
			color: var(--icon-subtle);
		}
	}

	&--translucent {
		background: color-mix(in srgb, var(--shade-2) 20%, transparent);
		color: var(--shade-1);
	}

	// These styles are currently not used on the marketing site
	// If you need these on the marketing site, let's have a conversation about it
	html:not(.website) & {

		&--alt {
			background-color: var(--shade-3);
			outline-color: var(--shade-6);
		}

		// States
		&--alert {
			background-color: var(--alert-7);
			color: var(--text-alert);
		}

		&--error {
			background-color: var(--bg-error);
			outline-color: var(--border-error);
			color: var(--text-error);
		}

		&--warning {
			background: linear-gradient(var(--bg-warning), var(--bg-warning)), var(--bg-primary); // layer colors to avoid transparency issues with table row striping
			outline-color: var(--border-warning);
			color: var(--text-warning);
		}

		&--success {
			background-color: var(--bg-success);
			outline-color: var(--border-success);
			color: var(--text-success);
		}
	}

	// The platform classes are on <html>, so they have to be combined with the html selector rather than nested inside it
	html.is-mac:not(.website) &,
	html.is-ios:not(.website) & {

		&--error {
			box-shadow: 0 0 0 1px var(--border-error);
		}

		&--warning {
			box-shadow: 0 0 0 1px var(--border-warning);
		}

		&--success {
			box-shadow: 0 0 0 1px var(--border-success);
		}
	}
}
</style>